import type { ReactNode } from "react";

import type { DataTableEditConfig } from "@/components/table/columns";

/**
 * The record-page shell's whole configuration surface (mirroring
 * `components/table/columns.ts`'s `DataTableColumn` — "configured by a
 * column list and nothing else", applied to its sibling component per
 * spec judgement call in the Code Map). `key` is constrained to
 * `keyof Row & string`, the generated API type's own field names, so a
 * field naming a nonexistent property fails the TypeScript build the same
 * way an invalid table column does.
 *
 * There is deliberately no `label` here either — display names come from
 * the one label map (`lib/field-labels.ts`), looked up by `key`.
 *
 * `edit` reuses `DataTableEditConfig` — the exact type Story 2.3's inline
 * editing already defined — so a field row is provably the same
 * commit/conflict/CSRF mechanism at row granularity, not a second one
 * (spec judgement call 6).
 */
export interface RecordField<Row> {
  /** A real field name on the row's generated API type. */
  key: keyof Row & string;
  /** Custom value content, the same shape as `DataTableColumn.render`. */
  render?: (row: Row) => ReactNode;
  /** Makes this field row inline-editable — Story 2.3's mechanism, reused
   * wholesale, not reimplemented. Absent means read-only. */
  edit?: DataTableEditConfig<Row>;
  /**
   * Renders immediately, above "More details" (spec judgement call 7: "a
   * property never presents 25 inputs at once"). Defaults to `true` — a
   * field must opt *out* of primary placement, so a field list that never
   * thinks about disclosure at all renders exactly as it did before this
   * mechanism existed.
   */
  primary?: boolean;
  /**
   * AD-10's provenance (spec judgement call 1) — a `Resolved`-shaped
   * `{tier, confirmedOn}` this field's value carries, or `undefined` for a
   * field with no legal-default provenance (every real field on
   * `UserResource` today). Renders the dotted-underline warning and the
   * exact sentence when `tier === "app_default"` or `confirmedOn` is
   * `null`; plain otherwise. A function of the row, not a static value, so
   * a real field (Epic 5's) can vary its provenance per record.
   */
  provenance?: (row: Row) => RecordFieldProvenance | undefined;
  /**
   * Overrides `PROVENANCE_WARNING_SENTENCE` for this field's own warning
   * marker. AD-10's verbatim sentence (EXPERIENCE.md § Provenance and Legal
   * Language) is explicitly and only about a *date* — every existing
   * `provenance` caller (Municipality, Lease, Renewal, and Property's own
   * date fields) is a real legally-derived date, so the shared constant
   * stays their correct, unmodified default. A non-date field with the same
   * "unconfirmed municipal default" provenance (Property's twelve resolved
   * settings include money, URLs, names, and day-counts alongside one real
   * date) supplies its own type-appropriate wording here instead of
   * inheriting a sentence that says "date" over a dollar figure.
   */
  warningSentence?: string;
}

export interface RecordFieldProvenance {
  tier: "app_default" | "municipality" | "property_override" | "confirmed";
  confirmedOn: string | null;
  /**
   * The resolver's own real `usable` flag (`app/platform/settings_resolver.
   * Resolved.usable`, Story 5.3 Tier A fix) — present whenever a caller has
   * a genuine one to report. `tier` and `confirmedOn` alone cannot
   * distinguish every real state: `Municipality.confirmed_on` and
   * `Municipality.setup_complete` are independent, separately-PATCHable
   * columns with no correlation between them, so a real, reachable state
   * exists — `tier="municipality"`, `confirmedOn` a real date, but
   * `usable=false` (the town's setup checklist is still incomplete) — that
   * neither `tier === "app_default"` nor `confirmedOn === null` catches.
   * `usable` is the one flag the backend explicitly computed to answer "is
   * this trustworthy yet", so it is the correct primary signal wherever a
   * caller has it.
   *
   * `undefined` for a caller with no real `usable` to give —
   * `MunicipalityRecord.tsx`'s own field-level provenance derives `tier`/
   * `confirmedOn` directly from the municipality row's own nullability
   * (spec judgement call 7) rather than from a call to `resolve()` (that
   * page IS the municipality record `resolve()` would read from, not a
   * property inheriting from one), so it has no independent `usable` value
   * to report. `undefined` here deliberately means "no signal", not "not
   * usable" — `needsProvenanceWarning` below falls back to the original
   * `tier`/`confirmedOn` rule only in that case, so that page's own
   * behaviour is unchanged.
   */
  usable?: boolean;
}

/** AD-10 / EXPERIENCE.md § Provenance and Legal Language's verbatim sentence
 * — defined once, here, so no field-row rendering ever paraphrases it. */
export const PROVENANCE_WARNING_SENTENCE =
  "This date is a PropDesk default and has not been confirmed for this property.";

/** Whether a field's provenance should render the warning marker (Story
 * 2.5 spec judgement call 1: "when `tier === 'app_default'` or
 * `confirmedOn` is null"). Exported so both `FieldRow` and its tests share
 * the one rule.
 *
 * `tier === "property_override"` is a deliberate, explicit exception to
 * the plain `confirmedOn === null` rule (Story 5.3 fix — the first real
 * caller to ever produce this tier through this mechanism; Story 2.5 wrote
 * the rule above before `property_override` existed to test against, and
 * Users/Municipality never produce it). `app/platform/settings_resolver.py`
 * documents `confirmed_on` as *always* `None` for this tier (Story 5.1
 * judgement call 3) — a structural fact of the tier itself, not "this one
 * hasn't been confirmed yet" — while `usable` is unconditionally `True` for
 * it (an explicit human override for this one property is inherently
 * trustworthy, AD-9). Without this exception, `confirmedOn === null` alone
 * would flag *every* override with "This date is a PropDesk default and
 * has not been confirmed for this property." — actively wrong: an override
 * is the opposite of a PropDesk default, and the sentence would render on
 * a value the account holder personally typed in.
 *
 * Story 5.3 Tier A fix: when `provenance.usable` is present, it is the
 * primary signal — `usable === false` warns, full stop, regardless of
 * `tier`/`confirmedOn`'s own values (a `tier="municipality"` value with a
 * real `confirmedOn` date is not automatically trustworthy; `usable` is the
 * one flag the resolver computed specifically to answer that). Only a
 * caller with no real `usable` to give (`undefined` — see `fields.ts`'s own
 * doc comment on the field) falls back to the original `tier`/`confirmedOn`
 * proxy rule below, preserving `MunicipalityRecord.tsx`'s existing,
 * unrelated behaviour exactly. */
export function needsProvenanceWarning(provenance: RecordFieldProvenance | undefined): boolean {
  if (provenance === undefined) {
    return false;
  }
  if (provenance.tier === "property_override") {
    return false;
  }
  if (provenance.usable !== undefined) {
    return provenance.usable === false;
  }
  return provenance.tier === "app_default" || provenance.confirmedOn === null;
}
